import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SequencePathBar from './SequencePathBar';
import GoogleOneTapPrompt from './auth/GoogleOneTapPrompt';
import { organisationApi, publicEnquiryApi } from '../services/api';
import { isValidEmail } from '../utils/emailValidation';
import {
  clearGuestHireDraft,
  clearGuestSession,
  getGuestHireDraft,
  getGuestSession,
  setGuestHireDraft,
  setGuestSession,
} from '../services/authStorage';
import './VenueExperience.css';

const defaultOrganisation = { id: 'daeast', name: "Da'East Studios" };

const getDashboardPath = (user) => {
  switch (user?.role) {
    case 'admin':
    case 'super_admin':
      return '/app/event-simple';
    case 'teacher':
      return '/app/teacher/schedule';
    case 'event_simple':
      return '/app/event-simple';
    case 'simple_room_controls':
      return '/app/room-controls';
    default:
      return '/app/profile';
  }
};

const buildFallbackRooms = (organisationName) => [
  {
    id: 101,
    name: 'Studio 1',
    organisation_id: defaultOrganisation.id,
    organisation_name: organisationName,
    floor_area: '611 sq ft',
    hourly_rate: 'From £28 / hour',
    suitable_for: ['Dance classes', 'Rehearsals', 'Private practice'],
    features: ['Sprung wooden floor', 'Full-length mirrors', 'Integrated sound system'],
    description:
      'A bright, highly adaptable studio that works well for technique classes, rehearsal blocks, and small group sessions.',
    image:
      'linear-gradient(135deg, rgba(255, 196, 0, 0.18), rgba(255, 111, 0, 0.08)), radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 38%), #151515',
    public_status: 'Ready for bookings',
    public_status_note: 'Availability confirmed during enquiry review',
  },
  {
    id: 102,
    name: 'Studio 2',
    organisation_id: defaultOrganisation.id,
    organisation_name: organisationName,
    floor_area: '748 sq ft',
    hourly_rate: 'From £34 / hour',
    suitable_for: ['Workshops', 'Fitness sessions', 'Training events'],
    features: ['Extra floor space', 'Wall-length mirrors', 'Portable barres available'],
    description:
      'A larger-format room for workshops, visiting teachers, and sessions that need more movement range or audience flexibility.',
    image:
      'linear-gradient(135deg, rgba(255, 89, 94, 0.16), rgba(21, 21, 21, 0.12)), radial-gradient(circle at 75% 20%, rgba(255,255,255,0.22), transparent 32%), #1b1b1b',
    public_status: 'High demand',
    public_status_note: 'Popular evening and weekend slot',
  },
  {
    id: 103,
    name: 'Creative Room',
    organisation_id: defaultOrganisation.id,
    organisation_name: organisationName,
    floor_area: '420 sq ft',
    hourly_rate: 'From £22 / hour',
    suitable_for: ['Meetings', 'Small rehearsals', 'Coaching'],
    features: ['Flexible setup', 'Quiet breakout feel', 'Presentation screen option'],
    description:
      'A smaller, focused environment that suits planning sessions, auditions, one-to-one teaching, and creative development work.',
    image:
      'linear-gradient(135deg, rgba(51, 214, 166, 0.18), rgba(255, 196, 0, 0.08)), radial-gradient(circle at 60% 30%, rgba(255,255,255,0.18), transparent 34%), #111111',
    public_status: 'Best for daytime hire',
    public_status_note: 'Ideal for low-footfall sessions',
  },
];

const initialFormState = {
  first_name: '',
  last_name: '',
  group_business_name: '',
  phone: '',
  organisation_id: '',
  preferred_room_id: '',
  nature_of_use: 'dance_class',
  date_time_from: '',
  date_time_to: '',
  one_off_or_regular: 'one_off',
  message: '',
  how_did_you_find_us: 'google',
  terms_accepted: false,
  company_id: '',
};

const buildInitialFormState = (organisationId = '', roomId = '') => ({
  ...initialFormState,
  organisation_id: organisationId ? String(organisationId) : '',
  preferred_room_id: roomId ? String(roomId) : '',
});

const natureOfUseOptions = [
  { value: 'dance_class', label: 'Dance class' },
  { value: 'dance_workshop', label: 'Dance workshop' },
  { value: 'fitness_class', label: 'Fitness class' },
  { value: 'private_lesson', label: 'Private lesson / practice' },
  { value: 'rehearsal', label: 'Rehearsal' },
  { value: 'training_seminar', label: 'Training session / seminar' },
  { value: 'other', label: 'Other' },
];

const discoveryOptions = [
  { value: 'google', label: 'Google' },
  { value: 'social_media', label: 'Social media' },
  { value: 'word_of_mouth', label: 'Word of mouth' },
  { value: 'existing_member', label: 'Existing member' },
  { value: 'other', label: 'Other' },
];

const recurrenceOptions = [
  { value: 'one_off', label: 'One-off' },
  { value: 'regular_weekly', label: 'Regular weekly' },
  { value: 'regular_fortnightly', label: 'Regular fortnightly' },
  { value: 'regular_monthly', label: 'Regular monthly' },
];

const normalizeRoomList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return fallback;
};

const normalizeRoom = (room, organisationName) => {
  const hireSettings = room.settings?.public_hire || room.settings?.publicHire || room.public_hire || room.publicHire || {};

  return {
    id: room.id,
    name: room.name,
    organisation_id: room.organisation_id,
    organisation_name: room.organisation_name || organisationName,
    floor_area: room.floor_area || room.floorArea || 'Room size on request',
    hourly_rate:
      room.public_pricing_text ||
      room.publicPricingText ||
      hireSettings.hourly_rate_text ||
      hireSettings.hourlyRateText ||
      (room.hourly_rate != null
        ? `From £${Number(room.hourly_rate).toFixed(0)} / hour`
        : room.hourlyRateText || 'Pricing available on enquiry'),
    suitable_for: normalizeRoomList(
      room.suitable_for || room.suitableFor || hireSettings.suitable_for || hireSettings.suitableFor,
      ['Classes', 'Rehearsals', 'Events']
    ),
    features: normalizeRoomList(room.features || hireSettings.features),
    description: room.description || hireSettings.description || 'A versatile room available for creative and community-led use.',
    image: room.coverPhotoUrl || room.cover_photo_url || hireSettings.coverPhotoUrl || hireSettings.cover_photo_url || '',
    public_status:
      room.public_status || room.publicStatus || hireSettings.public_status || hireSettings.publicStatus || 'Enquire for availability',
    public_status_note:
      room.public_status_note ||
      room.publicStatusNote ||
      hireSettings.public_status_note ||
      hireSettings.publicStatusNote ||
      'Booking requests reviewed by the studio team',
    public_notes:
      room.public_notes || room.publicNotes || hireSettings.public_notes || hireSettings.publicNotes || hireSettings.notes || '',
  };
};

const stripCapturedGuestParams = (params) => {
  params.delete('guestToken');
  params.delete('guest_token');
  params.delete('email');

  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, document.title, nextUrl);
};

const formatEnquiryDateTime = (value) => {
  if (!value) {
    return 'To be confirmed';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatEnquiryStatus = (status) =>
  String(status || 'submitted')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const toApiDateTime = (value) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

const VenueHirePage = () => {
  const bookingSectionRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, loginWithGoogle } = useAuth();

  const [isReady, setIsReady] = useState(false);
  const [organisations, setOrganisations] = useState([defaultOrganisation]);
  const [selectedOrgId, setSelectedOrgId] = useState(String(defaultOrganisation.id));
  const [rooms, setRooms] = useState(buildFallbackRooms(defaultOrganisation.name));
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [usingFallbackRooms, setUsingFallbackRooms] = useState(true);
  const [selectedRoomId, setSelectedRoomId] = useState('');

  const [identityEmail, setIdentityEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityError, setIdentityError] = useState('');
  const [identityInfo, setIdentityInfo] = useState('');
  const [guestToken, setGuestToken] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [guestSessionExpiresAt, setGuestSessionExpiresAt] = useState('');

  const [formData, setFormData] = useState(buildInitialFormState());
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [myEnquiries, setMyEnquiries] = useState([]);
  const [enquiryTotal, setEnquiryTotal] = useState(0);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [enquiriesError, setEnquiriesError] = useState('');
  const [showMyEnquiries, setShowMyEnquiries] = useState(false);

  const handleGoogleOneTapCredential = async (response) => {
    setIdentityInfo('');
    try {
      const userData = await loginWithGoogle({
        idToken: response.credential,
        selectBy: response.select_by || response.selectBy,
      });
      if (userData?.pendingApproval) {
        setIdentityInfo(userData.message || 'Your account is waiting for admin approval.');
        return;
      }
      navigate(getDashboardPath(userData), { replace: true });
    } catch (err) {
      if (err?.code === 'LOGIN_METHOD_NOT_LINKED' && err.providerEmail) {
        setIdentityEmail(err.providerEmail);
        setIdentityInfo('Use email verification below or create an account to continue your room hire enquiry.');
        return;
      }
      console.debug('Google One Tap room-hire sign-in unavailable:', err.message || err);
    }
  };

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => setIsReady(true));
    return () => window.cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const storedGuestSession = getGuestSession();
    const storedGuestDraft = getGuestHireDraft();
    const token = params.get('guestToken') || params.get('guest_token') || storedGuestSession?.token;
    const email = params.get('email') || storedGuestSession?.email || storedGuestDraft?.identityEmail;
    const roomId = params.get('room') || storedGuestDraft?.selectedRoomId;
    const orgId = params.get('org') || storedGuestDraft?.selectedOrgId;

    if (token) {
      setGuestToken(token);
      setOtpRequested(true);
      clearGuestHireDraft();
    }
    if (email) {
      setVerifiedEmail(email);
      setIdentityEmail(email);
    }
    if (storedGuestSession?.expiresAt) {
      setGuestSessionExpiresAt(storedGuestSession.expiresAt);
    }
    if (roomId) {
      setSelectedRoomId(roomId);
    }
    if (orgId) {
      setSelectedOrgId(orgId);
    }

    if (params.has('guestToken') || params.has('guest_token') || params.has('email')) {
      setGuestSession({
        token,
        email,
        expiresAt: storedGuestSession?.expiresAt,
      });
      stripCapturedGuestParams(params);
    }
  }, []);

  useEffect(() => {
    if (!guestToken) {
      clearGuestSession();
      return;
    }

    setGuestSession({
      token: guestToken,
      email: verifiedEmail || identityEmail,
      expiresAt: guestSessionExpiresAt,
    });
  }, [guestToken, verifiedEmail, identityEmail, guestSessionExpiresAt]);

  useEffect(() => {
    setGuestHireDraft({
      selectedOrgId,
      selectedRoomId,
      identityEmail,
    });
  }, [identityEmail, selectedOrgId, selectedRoomId]);

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await organisationApi.getPublicList();
        const nextOrganisations = response.data?.data || response.data || [];
        if (nextOrganisations.length) {
          setOrganisations(nextOrganisations);
          setSelectedOrgId((current) => current || String(nextOrganisations[0].id));
        }
      } catch (error) {
        setOrganisations([defaultOrganisation]);
        setSelectedOrgId(String(defaultOrganisation.id));
      }
    };

    fetchOrganisations();
  }, []);

  useEffect(() => {
    if (!selectedOrgId) {
      return;
    }

    let isCancelled = false;

    const fetchRooms = async () => {
      setRoomsLoading(true);
      setUsingFallbackRooms(false);

      const organisationName =
        organisations.find((org) => String(org.id) === String(selectedOrgId))?.name || defaultOrganisation.name;

      try {
        const response = await publicEnquiryApi.getPublicRooms(selectedOrgId);
        const roomList = response.data?.data?.rooms || response.data?.data || response.data || [];

        if (!isCancelled && roomList.length) {
          setRooms(roomList.map((room) => normalizeRoom(room, organisationName)));
          setUsingFallbackRooms(false);
        } else if (!isCancelled) {
          setRooms(buildFallbackRooms(organisationName));
          setUsingFallbackRooms(true);
        }
      } catch (error) {
        if (!isCancelled) {
          setRooms(buildFallbackRooms(organisationName));
          setUsingFallbackRooms(true);
        }
      } finally {
        if (!isCancelled) {
          setRoomsLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrgId, organisations]);

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      organisation_id: selectedOrgId ? String(selectedOrgId) : '',
      preferred_room_id: selectedRoomId ? String(selectedRoomId) : current.preferred_room_id,
    }));
  }, [selectedOrgId, selectedRoomId]);

  const fetchMyEnquiries = React.useCallback(async () => {
    if (!guestToken) {
      setMyEnquiries([]);
      setEnquiryTotal(0);
      setEnquiriesError('');
      return;
    }

    setEnquiriesLoading(true);
    setEnquiriesError('');

    try {
      const response = await publicEnquiryApi.getMyEnquiries(guestToken);
      const payload = response.data?.data || response.data || {};
      const enquiries = payload.enquiries || [];

      setMyEnquiries(enquiries);
      setEnquiryTotal(payload.total || enquiries.length);
    } catch (error) {
      if (error.response?.status === 401) {
        clearGuestSession();
        setGuestToken('');
        setVerifiedEmail('');
        setGuestSessionExpiresAt('');
        setSubmitResult(null);
        setEnquiriesError('Your guest session expired. Verify your email again to continue.');
      } else {
        setEnquiriesError(
          error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'We could not load your previous enquiries right now.'
        );
      }
    } finally {
      setEnquiriesLoading(false);
    }
  }, [guestToken]);

  useEffect(() => {
    fetchMyEnquiries();
  }, [fetchMyEnquiries]);

  useEffect(() => {
    if (selectedRoomId && !rooms.some((room) => String(room.id) === String(selectedRoomId))) {
      setSelectedRoomId('');
    }
  }, [rooms, selectedRoomId]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sections = Array.from(document.querySelectorAll('.reveal-on-scroll'));

    if (prefersReducedMotion) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -10% 0px' }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [rooms, submitResult, guestToken]);

  const selectedOrganisation = useMemo(
    () => organisations.find((org) => String(org.id) === String(selectedOrgId)) || defaultOrganisation,
    [organisations, selectedOrgId]
  );

  const selectedRoom = useMemo(
    () => rooms.find((room) => String(room.id) === String(selectedRoomId)) || null,
    [rooms, selectedRoomId]
  );

  const journeySteps = [
    'Explore rooms',
    'Verify identity',
    'Send enquiry',
    'Create account',
    'Upload documents',
    'Request booking',
  ];

  const currentStep = submitResult ? 3 : guestToken ? 2 : selectedRoomId || otpRequested ? 1 : 0;
  const nextText = submitResult
    ? 'Next: create your account so the studio can move you toward booking approval.'
    : guestToken
      ? 'Next: send your enquiry and the team will reply with next steps.'
      : 'Next: verify your identity before the enquiry form unlocks.';

  const scrollToBooking = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bookingSectionRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const handleSelectRoom = (room) => {
    setSelectedRoomId(String(room.id));
    setSelectedOrgId(String(room.organisation_id || selectedOrganisation.id));
    scrollToBooking();
  };

  const handleIdentityProvider = (provider) => {
    setIdentityLoading(true);
    setIdentityInfo('');
    setIdentityError('');

    try {
      const startUrl = publicEnquiryApi.getGuestIdentityStartUrl(provider);

      window.location.assign(startUrl);
    } catch (error) {
      const providerName = provider === 'google' ? 'Google' : 'Apple';
      setIdentityLoading(false);
      setIdentityError(
        error.message || `${providerName} guest verification is not available right now. Use the email code option below.`
      );
    }
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    setIdentityError('');
    setIdentityInfo('');

    if (!isValidEmail(identityEmail)) {
      setIdentityError('Enter a valid email address to receive your one-time code.');
      return;
    }

    setIdentityLoading(true);

    try {
      await publicEnquiryApi.requestGuestOtp(identityEmail);
      setOtpRequested(true);
      setIdentityInfo('A one-time code has been sent to your email address.');
    } catch (error) {
      setIdentityError(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          'We could not send a code right now. Please try again shortly.'
      );
    } finally {
      setIdentityLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setIdentityError('');
    setIdentityInfo('');

    if (!otpCode.trim()) {
      setIdentityError('Enter the code from your email to continue.');
      return;
    }

    setIdentityLoading(true);

    try {
      const response = await publicEnquiryApi.verifyGuestOtp(identityEmail, otpCode);
      const data = response.data?.data || response.data || {};
      const token = data.guestToken || data.token;
      const email = data.email || identityEmail;

      if (!token) {
        throw new Error('Guest token missing from verification response.');
      }

      setGuestToken(token);
      setVerifiedEmail(email);
      setGuestSessionExpiresAt(data.expiresAt || '');
      setIdentityInfo('Identity verified. Your enquiry form is ready.');
      setOtpCode('');
    } catch (error) {
      setIdentityError(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'We could not verify that code. Please try again.'
      );
    } finally {
      setIdentityLoading(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData((current) => ({ ...current, [name]: nextValue }));

    if (formErrors[name]) {
      setFormErrors((current) => ({ ...current, [name]: '' }));
    }
  };

  const validateEnquiryForm = () => {
    const nextErrors = {};

    if (!formData.first_name.trim()) nextErrors.first_name = 'First name is required.';
    if (!formData.last_name.trim()) nextErrors.last_name = 'Last name is required.';
    if (!formData.organisation_id) nextErrors.organisation_id = 'Choose a studio location.';
    if (!formData.nature_of_use) nextErrors.nature_of_use = 'Choose the nature of use.';
    if (!formData.date_time_from) nextErrors.date_time_from = 'Select a start date and time.';
    if (!formData.date_time_to) nextErrors.date_time_to = 'Select an end date and time.';
    if (
      formData.date_time_from &&
      formData.date_time_to &&
      new Date(formData.date_time_to).getTime() <= new Date(formData.date_time_from).getTime()
    ) {
      nextErrors.date_time_to = 'End date and time must be after the start date and time.';
    }
    if (!formData.one_off_or_regular) nextErrors.one_off_or_regular = 'Choose one-off or regular.';
    if (!formData.how_did_you_find_us) nextErrors.how_did_you_find_us = 'Tell us how you found us.';
    if (!formData.terms_accepted) nextErrors.terms_accepted = 'You must accept the terms to continue.';

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitEnquiry = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!guestToken) {
      setSubmitError('Verify your identity before sending an enquiry.');
      return;
    }

    if (!validateEnquiryForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      const payload = {
        ...formData,
        organisation_id: Number(formData.organisation_id),
        preferred_room_id: formData.preferred_room_id ? Number(formData.preferred_room_id) : undefined,
        date_time_from: toApiDateTime(formData.date_time_from),
        date_time_to: toApiDateTime(formData.date_time_to),
      };

      const response = await publicEnquiryApi.submitEnquiry(guestToken, payload);
      const data = response.data?.data || response.data || {};

      setSubmitResult({
        enquiryId: data.enquiryId,
        reference: data.reference || `ENQ-${Date.now()}`,
        message: data.message || 'Your enquiry has been received. The studio team will be in touch shortly.',
      });
      setShowMyEnquiries(false);
      await fetchMyEnquiries();
    } catch (error) {
      if (error.response?.status === 401) {
        clearGuestSession();
        setGuestToken('');
        setVerifiedEmail('');
        setGuestSessionExpiresAt('');
      }

      setSubmitError(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          'We could not submit your enquiry. Please review your details and try again.'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleStartAnotherEnquiry = () => {
    setSubmitResult(null);
    setSubmitError('');
    setFormErrors({});
    setFormData(buildInitialFormState(selectedOrgId, selectedRoomId));
    setShowMyEnquiries(false);
    setIdentityInfo('Your guest session is still active, so you can send another enquiry without verifying again.');
  };

  const handleRegisterHandoff = () => {
    navigate('/register', {
      state: {
        fromHire: true,
        verifiedEmail,
        organisationId: selectedOrgId,
        organisationName: selectedOrganisation?.name,
        selectedRoomId,
        selectedRoomName: selectedRoom?.name,
        enquiryReference: submitResult?.reference,
      },
    });
  };

  return (
    <div className={`experience-page electric-wayfinding ${isReady ? 'is-ready' : ''}`}>
      <GoogleOneTapPrompt
        disabled={isAuthenticated}
        onCredential={handleGoogleOneTapCredential}
      />
      <header className="experience-topbar">
        <Link to="/" className="experience-brand-mark">
          <span className="experience-brand-mark__glyph">SA</span>
          <span className="experience-brand-mark__text">StudioAEye Venue Hire</span>
        </Link>

        <nav className="experience-topbar__nav" aria-label="Primary">
          <a href="#room-sections">Rooms</a>
          <a href="#booking-request">Booking request</a>
          <Link to="/register">Create account</Link>
          <Link to="/login">Login</Link>
        </nav>
      </header>

      <main>
        <section className="experience-hero experience-hero--hire">
          <div className="experience-hero__copy">
            <span className="experience-kicker">Electric Wayfinding concept</span>
            <h1>Venue hire with the room story first and the admin friction hidden.</h1>
            <p>
              Explore the rooms, pick the space that fits, then move through a guided enquiry flow that tells you
              exactly what happens next at every stage.
            </p>

            <div className="experience-hero__actions">
              <button type="button" className="experience-button experience-button--primary" onClick={scrollToBooking}>
                Start booking request
              </button>
              <a className="experience-button experience-button--ghost" href="#room-sections">
                Explore rooms first
              </a>
            </div>

            <SequencePathBar
              steps={journeySteps}
              currentStep={currentStep}
              nextText={nextText}
              className="experience-hero__path"
            />
          </div>

          <aside className="experience-hero__panel reveal-on-scroll is-visible">
            <div className="experience-pulse-card">
              <span className="experience-pulse-card__eyebrow">First-time user friendly</span>
              <h2>Your sequence path</h2>
              <ol>
                <li>Choose a room that fits your use.</li>
                <li>Verify your identity so the form unlocks.</li>
                <li>Send your enquiry.</li>
                <li>Create your account for onboarding and booking.</li>
              </ol>
            </div>

            <div className="experience-switcher-card">
              <label htmlFor="hire-org-selector">Studio location</label>
              <select
                id="hire-org-selector"
                className="experience-select"
                value={selectedOrgId}
                onChange={(event) => setSelectedOrgId(event.target.value)}
              >
                {organisations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <p>{usingFallbackRooms ? 'Showing curated room content while the public room feed is being finalized.' : 'Live venue content loaded.'}</p>
            </div>
          </aside>
        </section>

        <section className="experience-benefits-strip reveal-on-scroll" aria-label="Venue benefits">
          <span>Accessible transport links</span>
          <span>Regular booking discounts</span>
          <span>Clear next-step guidance</span>
          <span>Fast enquiry handoff into account creation</span>
        </section>

        <nav className="experience-room-index reveal-on-scroll" aria-label="Room sections">
          {rooms.map((room) => (
            <a key={room.id} href={`#room-${room.id}`}>
              {room.name}
            </a>
          ))}
          <a href="#booking-request">Booking request</a>
        </nav>

        <section id="room-sections" className="experience-room-sections">
          {roomsLoading ? (
            <div className="experience-loading-grid">
              {[0, 1].map((item) => (
                <div key={item} className="experience-loading-card" />
              ))}
            </div>
          ) : (
            rooms.map((room, index) => (
              <section
                key={room.id}
                id={`room-${room.id}`}
                className={`venue-room-section reveal-on-scroll ${index % 2 === 1 ? 'venue-room-section--reverse' : ''}`}
              >
                <div
                  className="venue-room-section__media"
                  style={room.image && !room.image.startsWith('linear') && !room.image.startsWith('radial') ? { backgroundImage: `url(${room.image})` } : { background: room.image }}
                >
                  <span className="venue-room-section__status">{room.public_status}</span>
                </div>

                <div className="venue-room-section__body">
                  <span className="experience-kicker">{room.organisation_name}</span>
                  <h2>{room.name}</h2>

                  <div className="venue-room-section__meta">
                    <span>{room.floor_area}</span>
                    <span>{room.hourly_rate}</span>
                    <span>{room.public_status_note}</span>
                  </div>

                  <div className="venue-room-section__tags">
                    {room.suitable_for.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>

                  <p>{room.description}</p>

                  {room.public_notes ? <p><strong>Notes:</strong> {room.public_notes}</p> : null}

                  <ul className="venue-room-section__features">
                    {room.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>

                  <div className="venue-room-section__actions">
                    <button
                      type="button"
                      className="experience-button experience-button--primary"
                      onClick={() => handleSelectRoom(room)}
                    >
                      Enquire about this room
                    </button>
                    <a className="experience-button experience-button--ghost" href="#booking-request">
                      Jump to form
                    </a>
                  </div>
                </div>
              </section>
            ))
          )}
        </section>

        <section ref={bookingSectionRef} id="booking-request" className="experience-booking-block reveal-on-scroll">
          <div className="experience-booking-block__header">
            <span className="experience-kicker">Booking request form</span>
            <h2>Tell us which room you need, then we will guide you through the next steps.</h2>
            <p>
              The form stays simple, but the sequence path stays visible so you always know what happens after this.
            </p>
          </div>

          <div className="experience-booking-grid">
            <div className="experience-booking-main">
              <SequencePathBar steps={journeySteps} currentStep={currentStep} nextText={nextText} />

              <div
                key={submitResult ? 'submitted' : guestToken ? 'verified' : otpRequested ? 'otp' : 'unverified'}
                className="experience-state-panel"
              >
                {submitResult ? (
                  <div className="experience-success-panel" role="status" aria-live="polite">
                    <span className="experience-success-panel__badge">Enquiry sent</span>
                    <h3>Your venue enquiry is in motion.</h3>
                    <p>{submitResult.message}</p>
                    <div className="experience-reference-card">
                      <span>Reference</span>
                      <strong>{submitResult.reference}</strong>
                    </div>

                    <div className="experience-success-panel__actions">
                      <button type="button" className="experience-button experience-button--primary" onClick={handleRegisterHandoff}>
                        Register to continue booking
                      </button>
                      <button
                        type="button"
                        className="experience-button experience-button--outline"
                        onClick={() => setShowMyEnquiries((current) => !current)}
                      >
                        {showMyEnquiries ? 'Hide my enquiries' : 'View my enquiries'}
                      </button>
                      <button
                        type="button"
                        className="experience-button experience-button--ghost"
                        onClick={handleStartAnotherEnquiry}
                      >
                        Start another enquiry
                      </button>
                    </div>
                  </div>
                ) : guestToken ? (
                  <form className="experience-form" onSubmit={handleSubmitEnquiry}>
                    <div className="experience-summary-pill">
                      {selectedRoom ? `Enquiring about ${selectedRoom.name}` : `Studio selected: ${selectedOrganisation.name}`}
                    </div>

                    <div className="experience-form-grid">
                      <label>
                        <span>First name</span>
                        <input
                          className={`experience-input ${formErrors.first_name ? 'has-error' : ''}`}
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleFormChange}
                        />
                        {formErrors.first_name ? <small>{formErrors.first_name}</small> : null}
                      </label>

                      <label>
                        <span>Last name</span>
                        <input
                          className={`experience-input ${formErrors.last_name ? 'has-error' : ''}`}
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleFormChange}
                        />
                        {formErrors.last_name ? <small>{formErrors.last_name}</small> : null}
                      </label>

                      <label>
                        <span>Group / business name</span>
                        <input className="experience-input" name="group_business_name" value={formData.group_business_name} onChange={handleFormChange} />
                      </label>

                      <label>
                        <span>Verified email</span>
                        <input className="experience-input" value={verifiedEmail} readOnly />
                      </label>

                      <label>
                        <span>Phone number</span>
                        <input className="experience-input" name="phone" value={formData.phone} onChange={handleFormChange} />
                      </label>

                      <label>
                        <span>Studio location</span>
                        <select
                          className={`experience-select ${formErrors.organisation_id ? 'has-error' : ''}`}
                          name="organisation_id"
                          value={formData.organisation_id}
                          onChange={(event) => {
                            handleFormChange(event);
                            setSelectedOrgId(event.target.value);
                          }}
                        >
                          <option value="">Select a studio location</option>
                          {organisations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        {formErrors.organisation_id ? <small>{formErrors.organisation_id}</small> : null}
                      </label>

                      <label>
                        <span>Preferred room</span>
                        <select className="experience-select" name="preferred_room_id" value={formData.preferred_room_id} onChange={handleFormChange}>
                          <option value="">Any suitable room</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span>Nature of use</span>
                        <select
                          className={`experience-select ${formErrors.nature_of_use ? 'has-error' : ''}`}
                          name="nature_of_use"
                          value={formData.nature_of_use}
                          onChange={handleFormChange}
                        >
                          {natureOfUseOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span>Date / time from</span>
                        <input
                          className={`experience-input ${formErrors.date_time_from ? 'has-error' : ''}`}
                          type="datetime-local"
                          name="date_time_from"
                          value={formData.date_time_from}
                          onChange={handleFormChange}
                        />
                        {formErrors.date_time_from ? <small>{formErrors.date_time_from}</small> : null}
                      </label>

                      <label>
                        <span>Date / time to</span>
                        <input
                          className={`experience-input ${formErrors.date_time_to ? 'has-error' : ''}`}
                          type="datetime-local"
                          name="date_time_to"
                          value={formData.date_time_to}
                          onChange={handleFormChange}
                        />
                        {formErrors.date_time_to ? <small>{formErrors.date_time_to}</small> : null}
                      </label>

                      <label>
                        <span>One-off / regular</span>
                        <select
                          className={`experience-select ${formErrors.one_off_or_regular ? 'has-error' : ''}`}
                          name="one_off_or_regular"
                          value={formData.one_off_or_regular}
                          onChange={handleFormChange}
                        >
                          {recurrenceOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span>How did you find us?</span>
                        <select
                          className={`experience-select ${formErrors.how_did_you_find_us ? 'has-error' : ''}`}
                          name="how_did_you_find_us"
                          value={formData.how_did_you_find_us}
                          onChange={handleFormChange}
                        >
                          {discoveryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="experience-form-grid__full">
                        <span>Message</span>
                        <textarea className="experience-textarea" name="message" rows="5" value={formData.message} onChange={handleFormChange} />
                      </label>

                      <label className="experience-checkbox experience-form-grid__full">
                        <input type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleFormChange} />
                        <span>
                          I agree to the terms and conditions and understand this sends an enquiry, not a confirmed booking.
                        </span>
                      </label>
                      {formErrors.terms_accepted ? <small className="experience-inline-error">{formErrors.terms_accepted}</small> : null}

                      <input type="text" name="company_id" value={formData.company_id} onChange={handleFormChange} className="experience-honeypot" tabIndex="-1" autoComplete="off" />
                    </div>

                    {submitError ? <div className="experience-alert experience-alert--error">{submitError}</div> : null}

                    <div className="experience-form-actions">
                      <button type="submit" className="experience-button experience-button--primary" disabled={submitLoading}>
                        {submitLoading ? 'Sending enquiry...' : 'Send booking request'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="experience-identity-panel">
                    <div className="experience-identity-panel__header">
                      <span className="experience-kicker">Step 2</span>
                      <h3>Verify your identity to unlock the enquiry form.</h3>
                      <p>Your email verification protects the venue team from spam and keeps your next steps connected.</p>
                    </div>

                    <div className="experience-identity-actions">
                      <button type="button" className="experience-button experience-button--primary" onClick={() => handleIdentityProvider('google')}>
                        Continue with Google
                      </button>
                      <button type="button" className="experience-button experience-button--outline" onClick={() => handleIdentityProvider('apple')}>
                        Continue with Apple
                      </button>
                    </div>

                    <div className="experience-divider"><span>or verify by email</span></div>

                    <form className="experience-otp-form" onSubmit={otpRequested ? handleVerifyOtp : handleRequestOtp} noValidate>
                      <label>
                        <span>Email address</span>
                        <input
                          className={`experience-input ${identityError ? 'has-error' : ''}`}
                          type="email"
                          value={identityEmail}
                          onChange={(event) => setIdentityEmail(event.target.value)}
                          placeholder="you@example.com"
                        />
                      </label>

                      {otpRequested ? (
                        <label>
                          <span>One-time code</span>
                          <input
                            className={`experience-input ${identityError ? 'has-error' : ''}`}
                            value={otpCode}
                            onChange={(event) => setOtpCode(event.target.value)}
                            placeholder="Enter the 6-digit code"
                          />
                        </label>
                      ) : null}

                      {identityInfo ? <div className="experience-alert experience-alert--info">{identityInfo}</div> : null}
                      {identityError ? <div className="experience-alert experience-alert--error">{identityError}</div> : null}

                      <div className="experience-form-actions">
                        <button type="submit" className="experience-button experience-button--primary" disabled={identityLoading}>
                          {identityLoading ? 'Working...' : otpRequested ? 'Verify code' : 'Email me a code'}
                        </button>
                        {otpRequested ? (
                          <button type="button" className="experience-button experience-button--ghost" onClick={handleRequestOtp} disabled={identityLoading}>
                            Resend code
                          </button>
                        ) : null}
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {showMyEnquiries ? (
                <div className="experience-enquiries-panel" aria-live="polite">
                  <div className="experience-enquiries-panel__header">
                    <div>
                      <span className="experience-kicker">Guest session history</span>
                      <h4>My enquiries</h4>
                      <p>
                        {enquiryTotal > 0
                          ? `${enquiryTotal} enquiry${enquiryTotal === 1 ? '' : 'ies'} linked to ${verifiedEmail || identityEmail}.`
                          : 'No enquiries have been recorded for this guest session yet.'}
                      </p>
                    </div>
                    <button type="button" className="experience-button experience-button--ghost" onClick={fetchMyEnquiries} disabled={enquiriesLoading}>
                      {enquiriesLoading ? 'Refreshing...' : 'Refresh list'}
                    </button>
                  </div>

                  {enquiriesError ? <div className="experience-alert experience-alert--error">{enquiriesError}</div> : null}

                  {enquiriesLoading && !myEnquiries.length ? <p className="experience-enquiries-empty">Loading your enquiry history…</p> : null}

                  {!enquiriesLoading && !myEnquiries.length && !enquiriesError ? (
                    <p className="experience-enquiries-empty">Your enquiry history will appear here once you have submitted a request.</p>
                  ) : null}

                  {myEnquiries.length ? (
                    <div className="experience-enquiries-list">
                      {myEnquiries.map((enquiry) => (
                        <article key={enquiry.id} className="experience-enquiry-card">
                          <div className="experience-enquiry-card__top">
                            <strong>{enquiry.reference}</strong>
                            <span className="experience-enquiry-status">{formatEnquiryStatus(enquiry.status)}</span>
                          </div>

                          <div className="experience-enquiry-card__meta">
                            <span>{enquiry.organisation_name}</span>
                            <span>{enquiry.preferred_room || 'Any suitable room'}</span>
                            <span>{formatEnquiryDateTime(enquiry.created_at)}</span>
                          </div>

                          <p className="experience-enquiry-card__summary">
                            {enquiry.nature_of_use || enquiry.intended_use || 'Room hire enquiry'}
                            {enquiry.date_time_from ? ` · ${formatEnquiryDateTime(enquiry.date_time_from)}` : ''}
                            {enquiry.date_time_to ? ` to ${formatEnquiryDateTime(enquiry.date_time_to)}` : ''}
                          </p>
                        </article>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <aside className="experience-booking-aside">
              <div className="experience-side-card">
                <span className="experience-side-card__eyebrow">What happens next</span>
                <ol>
                  <li>We review your enquiry and confirm fit.</li>
                  <li>You create your account with the same email.</li>
                  <li>You upload documents for onboarding approval.</li>
                  <li>You request the actual booking inside the app.</li>
                </ol>
              </div>

              <div className="experience-side-card">
                <span className="experience-side-card__eyebrow">Current context</span>
                <dl>
                  <div>
                    <dt>Studio</dt>
                    <dd>{selectedOrganisation.name}</dd>
                  </div>
                  <div>
                    <dt>Room</dt>
                    <dd>{selectedRoom?.name || 'Any suitable room'}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{guestToken ? 'Identity verified' : 'Identity check needed'}</dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VenueHirePage;
